<template>
  <div class="left-section">
    <div class="container">
      <!-- Header Section -->
      <div class="header-section">
        <div class="header-content">
          <h2 class="section-title">
            <span class="title-icon">💬</span>
            Send Message
          </h2>
          <p class="section-subtitle">Choose your preferred communication method</p>
        </div>
        <div class="header-decoration"></div>
      </div>

      <!-- Categories Grid -->
      <div class="categories-grid">
        <div
          v-for="(category, index) in categories"
          :key="category.title"
          class="category-card"
          :class="{ 
            'active': activeCategory === category.title,
            'animate-in': isVisible
          }"
          :style="{ '--delay': index * 0.1 + 's' }"
          @click="selectCategory(category.title)"
        >
          <div class="card-content">
            <div class="icon-container">
              <div class="icon-wrapper">
                <v-icon :color="activeCategory === category.title ? '#fff' : 'rgba(255,255,255,0.9)'" size="28">
                  {{ category.icon }}
                </v-icon>
              </div>
              <div class="icon-glow"></div>
            </div>
            
            <div class="card-info">
              <h3 class="card-title">{{ category.title }}</h3>
              <p class="card-description">{{ getCategoryDescription(category.title) }}</p>
            </div>
            
            <div class="card-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6l6 6-6 6-1.41-1.41z"/>
              </svg>
            </div>
          </div>
          
          <div class="card-background"></div>
          <div class="card-shine"></div>
          <div class="card-border-glow"></div>
        </div>
      </div>

    </div>

    <!-- Floating Elements -->
    <div class="floating-elements">
      <div class="floating-circle circle-1"></div>
      <div class="floating-circle circle-2"></div>
      <div class="floating-circle circle-3"></div>
      <div class="floating-circle circle-4"></div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ModernMessageSection",
  data() {
    return {
      isVisible: false,
      activeCategory: "Email",
      categories: [
        {
          title: "Email",
          icon: "mdi-email-outline",
          description: "Send professional emails with templates"
        },
        {
          title: "SMS",
          icon: "mdi-message-text-outline",
          description: "Quick text messages and notifications"
        },
        {
          title: "History",
          icon: "mdi-history",
          description: "View your message history and analytics"
        }
      ]
    };
  },
  
  computed: {
    ...mapState("auth", {
      profileType: "type"
    })
  },
  
  methods: {
    selectCategory(category) {
      this.activeCategory = category;
      this.$router.replace({
        query: { ...this.$route.query, category }
      });
    },
    
    getCategoryDescription(title) {
      const category = this.categories.find(c => c.title === title);
      return category ? category.description : '';
    }
  },
  
  mounted() {
    // Set default category
    if (!this.$route.query.category) {
      this.$router.replace({
        query: { ...this.$route.query, category: "Email" }
      });
    } else {
      this.activeCategory = this.$route.query.category;
    }
    
    // Trigger animations
    setTimeout(() => {
      this.isVisible = true;
    }, 100);
  }
};
</script>

<style scoped>
/* Base Styles */
.left-section {
  position: sticky;
  top: 60px;
  height: calc(70vh - 20px);
  width: 100%;
  max-width: 420px;
  min-width: 320px;
  background: linear-gradient(
    135deg,
    rgba(8, 68, 104, 0.8) 0%,
    rgba(59, 124, 161, 0.5) 50%,
    rgba(43, 176, 102, 0.3) 100%
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  position: relative;
  border-radius: 0 20px 20px 0;
}

.container {
  padding: 2rem 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  position: relative;
  z-index: 2;
  overflow-y: auto;
  scroll-behavior: smooth;
}

/* Custom Scrollbar */
.container::-webkit-scrollbar {
  width: 4px;
}

.container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* Header Section */
.header-section {
  position: relative;
  padding-bottom: 1.5rem;
}

.header-content {
  position: relative;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.title-icon {
  font-size: 1.5rem;
  filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.2));
}

.section-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  margin: 0;
  font-weight: 400;
}

.header-decoration {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(255, 255, 255, 0.6) 20%, 
    rgba(255, 255, 255, 0.8) 50%, 
    rgba(255, 255, 255, 0.6) 80%, 
    transparent 100%);
  border-radius: 1px;
}

/* Categories Grid */
.categories-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
}

.category-card {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  opacity: 0;
  transform: translateY(20px);
}

.category-card.animate-in {
  opacity: 1;
  transform: translateY(0);
  animation: slideInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) var(--delay) both;
}

.category-card:hover {
  transform: translateY(-3px) scale(1.01);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 
    0 10px 40px rgba(0, 0, 0, 0.2),
    0 4px 20px rgba(0, 0, 0, 0.1);
}

.category-card.active {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.category-card.active .card-title {
  color: #ffffff;
}

.category-card.active .card-description {
  color: rgba(255, 255, 255, 0.8);
}

.card-content {
  display: flex;
  align-items: center;
  padding: 1.25rem;
  gap: 1rem;
  position: relative;
  z-index: 2;
}

.icon-container {
  position: relative;
  flex-shrink: 0;
}

.icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.category-card.active .icon-wrapper {
  background: linear-gradient(135deg, 
    rgba(8, 68, 104, 0.9), 
    rgba(59, 124, 161, 0.7));
  border-color: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.category-card:hover .icon-wrapper {
  transform: scale(1.05);
  border-color: rgba(255, 255, 255, 0.2);
}

.icon-glow {
  position: absolute;
  inset: -4px;
  border-radius: 16px;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.2), 
    rgba(255, 255, 255, 0.05));
  opacity: 0;
  filter: blur(8px);
  transition: opacity 0.3s ease;
}

.category-card:hover .icon-glow,
.category-card.active .icon-glow {
  opacity: 1;
}

.card-info {
  flex: 1;
  min-width: 0;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 0.25rem 0;
  transition: color 0.3s ease;
}

.card-description {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.category-card:hover .card-description {
  color: rgba(255, 255, 255, 0.7);
}

.card-arrow {
  color: rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  transform: translateX(0);
  flex-shrink: 0;
}

.category-card:hover .card-arrow,
.category-card.active .card-arrow {
  color: rgba(255, 255, 255, 0.8);
  transform: translateX(4px);
}

.card-background {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.05), 
    rgba(255, 255, 255, 0.02));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.category-card:hover .card-background {
  opacity: 1;
}

.card-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255, 255, 255, 0.05), 
    transparent);
  transition: left 0.6s ease;
}

.category-card:hover .card-shine {
  left: 100%;
}

.card-border-glow {
  position: absolute;
  inset: -1px;
  border-radius: 16px;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.1),
    transparent 50%,
    rgba(255, 255, 255, 0.1));
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.category-card.active .card-border-glow {
  opacity: 1;
}

/* Stats Section */
.stats-section {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: auto;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.stat-item {
  text-align: center;
  flex: 1;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.stat-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-divider {
  width: 1px;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
}

/* Floating Elements */
.floating-elements {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.floating-circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.05), 
    rgba(255, 255, 255, 0.02));
  filter: blur(2px);
}

.circle-1 {
  width: 120px;
  height: 120px;
  top: 10%;
  right: -60px;
  animation: float 6s ease-in-out infinite;
}

.circle-2 {
  width: 80px;
  height: 80px;
  bottom: 30%;
  left: -40px;
  animation: float 8s ease-in-out infinite reverse;
}

.circle-3 {
  width: 60px;
  height: 60px;
  top: 60%;
  right: -30px;
  animation: float 7s ease-in-out infinite;
}

.circle-4 {
  width: 100px;
  height: 100px;
  bottom: 10%;
  right: 20%;
  animation: float 9s ease-in-out infinite 2s;
}

/* Animations */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg) scale(1);
  }
  50% {
    transform: translateY(-20px) rotate(180deg) scale(1.1);
  }
}

/* Responsive Design - Large Screens */
@media (max-width: 1200px) {
  .left-section {
    max-width: 360px;
    min-width: 280px;
  }
  
  .container {
    padding: 1.75rem 1.25rem;
    gap: 1.75rem;
  }
  
  .section-title {
    font-size: 1.5rem;
  }
}

/* Responsive Design - Tablets */
@media (max-width: 992px) {
  .left-section {
    max-width: 320px;
    min-width: 250px;
    top: 50px;
    height: calc(65vh - 20px);
  }
  
  .container {
    padding: 1.5rem 1rem;
    gap: 1.5rem;
  }
  
  .section-title {
    font-size: 1.35rem;
  }
  
  .section-subtitle {
    font-size: 0.85rem;
  }
  
  .card-content {
    padding: 1rem;
    gap: 0.875rem;
  }
  
  .icon-wrapper {
    width: 40px;
    height: 40px;
  }
  
  .card-title {
    font-size: 1rem;
  }
  
  .card-description {
    font-size: 0.8rem;
  }
  
  .stat-number {
    font-size: 1.25rem;
  }
  
  .stat-label {
    font-size: 0.65rem;
  }
}

/* Responsive Design - Small Tablets */
@media (max-width: 768px) {
  .left-section {
    position: relative;
    top: 0;
    height: auto;
    max-width: none;
    min-width: auto;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0;
    background: linear-gradient(
      135deg,
      rgba(8, 68, 104, 0.9) 0%,
      rgba(59, 124, 161, 0.6) 50%,
      rgba(43, 176, 102, 0.4) 100%
    );
  }
  
  .container {
    padding: 1.5rem;
    gap: 1.5rem;
    max-height: 500px;
  }
  
  .categories-grid {
    gap: 0.875rem;
  }
  
  .card-content {
    padding: 1rem 1.25rem;
  }
  
  .stats-section {
    padding: 1rem;
  }
  
  .stat-number {
    font-size: 1.1rem;
  }
  
  .floating-elements {
    display: none;
  }
}

/* Responsive Design - Mobile */
@media (max-width: 480px) {
  .left-section {
    background: linear-gradient(
      135deg,
      rgba(8, 68, 104, 0.95) 0%,
      rgba(59, 124, 161, 0.7) 50%,
      rgba(43, 176, 102, 0.5) 100%
    );
  }
  
  .container {
    padding: 1rem 0.875rem;
    gap: 1.25rem;
    max-height: 450px;
  }
  
  .header-section {
    padding-bottom: 1rem;
  }
  
  .section-title {
    font-size: 1.2rem;
  }
  
  .title-icon {
    font-size: 1.2rem;
  }
  
  .section-subtitle {
    font-size: 0.8rem;
  }
  
  .categories-grid {
    gap: 0.75rem;
  }
  
  .category-card {
    border-radius: 12px;
  }
  
  .card-content {
    padding: 0.875rem 1rem;
    gap: 0.75rem;
  }
  
  .icon-wrapper {
    width: 36px;
    height: 36px;
  }
  
  .card-title {
    font-size: 0.95rem;
  }
  
  .card-description {
    font-size: 0.75rem;
  }
  
  .card-arrow {
    display: none;
  }
  
  .stats-section {
    padding: 0.875rem;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  
  .stat-item {
    min-width: 60px;
  }
  
  .stat-number {
    font-size: 1rem;
  }
  
  .stat-label {
    font-size: 0.6rem;
  }
  
  .stat-divider {
    height: 20px;
  }
}

/* Responsive Design - Very Small Screens */
@media (max-width: 360px) {
  .container {
    padding: 0.75rem;
    gap: 1rem;
    max-height: 400px;
  }
  
  .section-title {
    font-size: 1rem;
  }
  
  .title-icon {
    font-size: 1rem;
  }
  
  .section-subtitle {
    font-size: 0.7rem;
  }
  
  .card-content {
    padding: 0.75rem;
    gap: 0.5rem;
  }
  
  .icon-wrapper {
    width: 32px;
    height: 32px;
  }
  
  .card-title {
    font-size: 0.85rem;
  }
  
  .card-description {
    font-size: 0.7rem;
  }
  
  .stats-section {
    padding: 0.75rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .stat-divider {
    width: 30px;
    height: 1px;
  }
  
  .stat-item {
    width: 100%;
  }
}

/* Landscape Orientation */
@media (max-height: 500px) and (orientation: landscape) {
  .left-section {
    height: auto;
    max-height: 80vh;
  }
  
  .container {
    max-height: none;
    padding: 1rem 1.5rem;
    gap: 1rem;
  }
  
  .header-section {
    padding-bottom: 0.75rem;
  }
  
  .section-title {
    font-size: 1.2rem;
  }
  
  .section-subtitle {
    font-size: 0.8rem;
  }
  
  .categories-grid {
    gap: 0.5rem;
  }
  
  .card-content {
    padding: 0.75rem 1rem;
  }
  
  .icon-wrapper {
    width: 32px;
    height: 32px;
  }
  
  .stats-section {
    padding: 0.75rem;
  }
  
  .stat-number {
    font-size: 1rem;
  }
}

/* Touch Device Optimizations */
@media (hover: none) {
  .category-card:hover {
    transform: none;
    border-color: rgba(255, 255, 255, 0.08);
  }
  
  .category-card:active {
    transform: scale(0.98);
  }
  
  .category-card:hover .card-shine {
    left: -100%;
  }
  
  .category-card:hover .icon-wrapper {
    transform: none;
  }
}

/* High DPI Screens */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .left-section {
    backdrop-filter: blur(30px);
    -webkit-backdrop-filter: blur(30px);
  }
  
  .header-decoration {
    height: 1px;
  }
  
  .card-border-glow {
    border-width: 0.5px;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .category-card {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
  
  .category-card:hover {
    transform: none !important;
  }
  
  .floating-circle {
    animation: none !important;
  }
  
  .card-shine {
    display: none !important;
  }
  
  .card-arrow {
    transition: none !important;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .left-section {
    background: linear-gradient(
      135deg,
      rgba(8, 68, 104, 0.9) 0%,
      rgba(59, 124, 161, 0.6) 50%,
      rgba(43, 176, 102, 0.4) 100%
    );
  }
}

/* Light Mode Support */
@media (prefers-color-scheme: light) {
  .left-section {
    background: linear-gradient(
      135deg,
      rgba(8, 68, 104, 0.85) 0%,
      rgba(59, 124, 161, 0.55) 50%,
      rgba(43, 176, 102, 0.35) 100%
    );
  }
}
</style>